import { create } from 'zustand';
import { produce } from 'immer';

import {
  fetchCountryDetails,
  fetchPersonAlertsDetails,
} from './personAlertApiStore';
import { useSaveChangesParamStore } from 'src/store/useSaveChangesParamStore';
import { fetchCaller } from 'src/store/apiStore/fetchCaller';

import { formatDateToRange } from 'src/utils/datepickerHelper';
import { commonFormatterFunction } from '../utils/dataComparisonHelper';
import { addressInfo, convertToNumber } from '../utils/personStoreHelper';
import {
  calculateAgeAsString,
  countryOptions,
  formattedPhoneNumber,
  getComments,
  isPhnIsInvalidOrNot,
  isTinymceEditorContentEmpty,
  isUsPhoneNumber,
} from 'src/utils/common';

import { sortItem } from '../utils/reactTableHelper';

import { AccountType } from 'src/types/AccountInfo';
import { GetPersonAlertsType } from '../types/personAlertsAccounts';
import { PersonAlertStore } from '../types/personAlertUser';
import { AddressType } from '../types/findPersonInformation';
import { MultiSelectInputType } from 'src/ui/components';

export const usePersonAlertStore = create<PersonAlertStore>((set, get) => ({
  alertDetails: {
    alertDate: '',
    Reasons: [],
    Person: {},
    CounselorOrCaseManager: '',
    SpecialProgramsParticipant: '',
    SituationDescription: '',
    TelephoneInterventionDescription: '',
  },
  countryInfo: [],
  accountsInfo: undefined,
  userMode: '',
  isEditApiLoading: false,
  hasCallLimit: false,
  isCallLimitExist: false,
  isPersonInformationLoading: false,
  isSaveClicked: false,
  showPrimaryNumberError: false,
  showSecondaryNumberError: false,
  initialAlert: {},
  currentData: {},
  newPerson: false,
  personApiCalled: false,
  searchPerson: '',
  showTable: false,
  selectedRowId: -1,
  currentSortItem: sortItem,
  // to check if the same account is selected again from the account selection table
  selectedAccountId: -1,
  listPageParam: 0,

  setListPageParam: (val: number) => {
    set(() => ({
      listPageParam: val,
    }));
  },

  optionsByAccount: {},
  AccountCategoryLimitingValues: [],

  setOptionsByAccount: (
    updater:
      | Record<number, Array<MultiSelectInputType>>
      | ((
          prev: Record<number, Array<MultiSelectInputType>>,
        ) => Record<number, Array<MultiSelectInputType>>),
  ) =>
    set(state => {
      const next =
        typeof updater === 'function'
          ? (updater as Function)(state.optionsByAccount)
          : updater;
      return {
        optionsByAccount: { ...(state.optionsByAccount || {}), ...next },
      };
    }),

  updateAlertDetails: (key: string, value: any) => {
    set(
      produce(state => {
        state.alertDetails = { ...state.alertDetails, [key]: value };
      }),
    );
  },

  //updating person object values
  updatePersonDetails: (key: string, value: string) => {
    set(
      produce(state => {
        if (key === 'PreferredName') {
          state.alertDetails.Person = {
            ...state.alertDetails.Person,
            PreferredName: value,
            PersonAlertDetails: {
              ...state.alertDetails.Person?.PersonAlertDetails,
              PreferredName: value,
            },
          };
        } else
          state.alertDetails.Person = {
            ...state.alertDetails.Person,
            [key]: value,
          };
      }),
    );
  },

  //to update Primaryaddress and phone details of person object
  updateOtherDetails: (
    key: string,
    value: any,
    PersonDetailKey: string | undefined,
  ) => {
    set(
      produce(state => {
        const person = state.alertDetails?.Person;
        const userMode = state?.userMode;

        if (!person) return;

        //CREATE + IsAllAlertsExpired → update Person directly
        if (userMode === '' && person.IsAllAlertsExpired) {
          if (PersonDetailKey) {
            person[PersonDetailKey] = {
              ...person[PersonDetailKey],
              [key]: value,
            };
          } else {
            person[key] = value;
          }
          return;
        }
        if (PersonDetailKey !== '') {
          if (
            state.alertDetails?.Person?.PersonAlertDetails === undefined ||
            state.alertDetails?.Person?.PersonAlertDetails === null
          ) {
            state.alertDetails.Person = {
              ...state?.alertDetails?.Person,
            };
            state.alertDetails.Person.PersonAlertDetails = {
              ...state?.alertDetails?.Person?.PersonAlertDetails,
              [`${PersonDetailKey}`]: {
                [key]: value,
              },
            };
          } else {
            state.alertDetails.Person.PersonAlertDetails[`${PersonDetailKey}`] =
              {
                ...state?.alertDetails?.Person?.PersonAlertDetails[
                  `${PersonDetailKey}`
                ],
                [key]: value,
              };
          }
        } else {
          state.alertDetails.Person.PersonAlertDetails = {
            ...state?.alertDetails?.Person?.PersonAlertDetails,
            [key]: value,
          };
        }
      }),
    );
  },

  //searched address data binding
  updateAddressDetails: (item: AddressType) => {
    const address = addressInfo(item);
    set(
      produce(state => {
        const person = state?.alertDetails?.Person;
        const userMode = state?.userMode;

        if (!person) return;

        //  CREATE + IsAllAlertsExpired → update Person directly
        if (userMode === '' && person.IsAllAlertsExpired) {
          person.PrimaryAddress = {
            ...(person.PrimaryAddress ?? {}),
            ...address,
          };
          return;
        }
        if (state?.alertDetails?.Person?.PersonAlertDetails === undefined) {
          state.alertDetails.Person = {
            ...state?.alertDetails?.Person,
          };
          state.alertDetails.Person.PersonAlertDetails = {
            ['PrimaryAddress']: {
              ...address,
            },
          };
        } else {
          state.alertDetails.Person.PersonAlertDetails.PrimaryAddress = {
            ...address,
          };
        }
      }),
    );
  },

  //searched person data binding
  updatePersonInformation: async (
    token: string,
    tokenType: string,
    accountId: number | undefined,
    personId: number,
  ) => {
    if (accountId && personId) {
      set(
        produce(state => {
          state.isPersonInformationLoading = true;
        }),
      );

      const response = await fetchCaller({
        token,
        tokenType,
        accountId,
        personId,
      });
      const countryList = countryOptions(get().countryInfo);
      const primaryPhone =
        response?.PersonAlertDetails === null
          ? response?.PrimaryPhone
          : response?.PersonAlertDetails?.PrimaryPhone;
      const secondaryPhone =
        response?.PersonAlertDetails === null
          ? response?.SecondaryPhone
          : response?.PersonAlertDetails?.SecondaryPhone;

      set(
        produce(state => {
          state.isPersonInformationLoading = false;
          state.showPrimaryNumberError = primaryPhone?.Number
            ? isPhnIsInvalidOrNot(
                primaryPhone?.Number,
                primaryPhone?.CountryId ?? 1,
                countryList,
              )
            : false;
          state.showSecondaryNumberError = isPhnIsInvalidOrNot(
            secondaryPhone?.Number,
            secondaryPhone?.CountryId ?? 1,
            countryList,
          );

          state.alertDetails.Person = {
            ...response,
          };

          if (response?.PersonAlertDetails === null) {
            state.alertDetails.Person.PersonAlertDetails = {
              ['PrimaryAddress']: { ...response?.PrimaryAddress },
              ['PrimaryPhone']: {
                ...response?.PrimaryPhone,
                Number: setPhoneNumber(
                  primaryPhone?.Number,
                  primaryPhone?.CountryId,
                ),
                CountryId: primaryPhone?.CountryId ?? 1,
                Comments: getComments(
                  primaryPhone,
                  primaryPhone?.CountryId,
                  get().countryInfo,
                ),
              },
              ['SecondaryPhone']: {
                ...response?.SecondaryPhone,
                Number: setPhoneNumber(
                  secondaryPhone?.Number,
                  secondaryPhone?.CountryId,
                ),
                CountryId: secondaryPhone?.CountryId ?? 1,
                Comments: getComments(
                  secondaryPhone,
                  secondaryPhone?.CountryId,
                  get().countryInfo,
                ),
              },
              ['Age']: response?.Age,
              ['Birthdate']: response?.Birthdate
                ? new Date(response?.Birthdate)
                : '',
              ['PreferredName']: response?.PreferredName,
            };
          } else {
            state.alertDetails.Person.PersonAlertDetails.Birthdate = response
              ?.PersonAlertDetails?.Birthdate
              ? new Date(response?.PersonAlertDetails?.Birthdate)
              : '';
            state.alertDetails.Person.PersonAlertDetails.PreferredName =
              response?.PersonAlertDetails?.PreferredName ?? '';
            state.alertDetails.Person.PersonAlertDetails.PrimaryPhone = {
              ...response.PersonAlertDetails?.PrimaryPhone,
              Number: setPhoneNumber(
                primaryPhone?.Number,
                primaryPhone?.CountryId,
              ),
              CountryId: primaryPhone?.CountryId ?? 1,
              Comments: primaryPhone?.Comments ?? null,
              // Comments: getComments(
              //   primaryPhone,
              //   primaryPhone?.CountryId,
              //   get().countryInfo,
              // ),
              //id key cannot be modified
              Id: response?.PersonAlertDetails?.PrimaryPhone?.Id,
            };

            state.alertDetails.Person.PersonAlertDetails.SecondaryPhone = {
              ...response.PersonAlertDetails?.SecondaryPhone,
              Number: setPhoneNumber(
                response?.PersonAlertDetails?.SecondaryPhone?.Number,
                response?.PersonAlertDetails?.SecondaryPhone?.CountryId,
              ),
              Comments: secondaryPhone?.Comments ?? null,
              // Comments: getComments(
              //   secondaryPhone,
              //   secondaryPhone?.CountryId,
              //   get().countryInfo,
              // ),
              CountryId:
                response?.PersonAlertDetails?.SecondaryPhone?.CountryId ?? 1,
              Id: response?.PersonAlertDetails?.SecondaryPhone?.Id,
            };
          }

          if (response?.PersonCallLimit !== null) {
            state.hasCallLimit = true;
          }
        }),
      );
    }
  },

  //edit alert data binding
  getPersonAlerts: async ({
    token,
    tokenType,
    accountId,
    id,
  }: Partial<GetPersonAlertsType>) => {
    const countryCode = await fetchCountryDetails({ token, tokenType });
    set(
      produce(state => {
        state.countryInfo = countryCode?.Data;
      }),
    );

    if (
      (get().userMode === 'Edit' &&
        useSaveChangesParamStore.getState().isDirty === false) ||
      useSaveChangesParamStore.getState().isSettingsPage === true
    ) {
      set(
        produce(state => {
          state.isEditApiLoading = true;
        }),
      );

      const response = await fetchPersonAlertsDetails({
        token: token,
        tokenType: tokenType,
        accountId: convertToNumber(accountId),
        id: convertToNumber(id),
      });

      if (response) {
        set(
          produce(state => {
            console.log('personalertresponse', response);
            state.accountsInfo = response?.Person?.Account;

            //for getting the limiter values already selected for an account
            if (response?.PersonAlertLimiterSettingValues) {
              const map: Record<number, Array<any>> = {};

              response.PersonAlertLimiterSettingValues.forEach(item => {
                const accountId = item.AccountCategoryLimitingValue.AccountId;

                if (!map[accountId]) {
                  map[accountId] = [];
                }

                map[accountId].push({
                  label: item.AccountCategoryLimitingValue.Name,
                  value: item.AccountCategoryLimitingValueId,
                });
              });

              state.optionsByAccount = map;
            }

            state.alertDetails = {
              ...response,

              Person: {
                ...response.Person,
                PersonAlertDetails: response.Person.PersonAlertDetails ?? {
                  PrimaryPhone: {},
                  SecondaryPhone: {},
                },
              },
              alertDate: formatDateToRange(
                response?.StartDate,
                response?.EndDate,
              ),
            };

            state.hasCallLimit = response?.Person?.PersonCallLimit !== null;
            state.isCallLimitExist = response?.Person?.PersonCallLimit !== null;
            if (response?.Person?.PersonAlertDetails?.Birthdate) {
              state.alertDetails.Person.PersonAlertDetails.Birthdate = new Date(
                response?.Person?.PersonAlertDetails?.Birthdate,
              );
            } else {
              state.alertDetails.Person.Birthdate = new Date(
                response?.Person?.Birthdate,
              );
            }

            // If `countryid` is null, then fill the `comments` field with the `phonenumber` from both primary and secondary.
            const primaryPhone =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.PrimaryPhone
                : response?.Person?.PersonAlertDetails?.PrimaryPhone;
            const primaryPhoneId =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.PrimaryPhoneId
                : response?.Person?.PersonAlertDetails?.PrimaryPhoneId;
            const secondaryPhone =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.SecondaryPhone
                : response?.Person?.PersonAlertDetails?.SecondaryPhone;
            const secondaryPhoneId =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.SecondaryPhoneId
                : response?.Person?.PersonAlertDetails?.SecondaryPhoneId;
            const primaryAddress =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.PrimaryAddress
                : response?.Person?.PersonAlertDetails?.PrimaryAddress;
            const primaryAddressId =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.PrimaryAddressId
                : response?.Person?.PersonAlertDetails?.PrimaryAddressId;
            const birthDate =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.Birthdate
                : response?.Person?.PersonAlertDetails?.Birthdate;
            const age =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.Age
                : response?.Person?.PersonAlertDetails?.Age;

            if (primaryPhone) {
              state.alertDetails.Person.PersonAlertDetails = {
                ...state.alertDetails.Person.PersonAlertDetails,
                PrimaryPhoneId: primaryPhoneId,
                PrimaryPhone: {
                  ...primaryPhone,
                  Comments: getComments(
                    primaryPhone,
                    primaryPhone?.CountryId,
                    get().countryInfo,
                  ),
                },
              };
            }
            if (secondaryPhone) {
              state.alertDetails.Person.PersonAlertDetails = {
                ...state.alertDetails.Person.PersonAlertDetails,
                SecondaryPhoneId: secondaryPhoneId,
                SecondaryPhone: {
                  ...secondaryPhone,
                  Comments: getComments(
                    secondaryPhone,
                    secondaryPhone?.CountryId,
                    get().countryInfo,
                  ),
                },
              };
            }

            if (primaryAddress) {
              state.alertDetails.Person.PersonAlertDetails = {
                ...state.alertDetails.Person.PersonAlertDetails,
                PrimaryAddressId: primaryAddressId,
                PrimaryAddress: {
                  ...primaryAddress,
                },
              };
            }

            if (birthDate) {
              state.alertDetails.Person.PersonAlertDetails = {
                ...state.alertDetails.Person.PersonAlertDetails,
                Birthdate: new Date(birthDate),
              };
            }

            if (age) {
              state.alertDetails.Person.PersonAlertDetails = {
                ...state.alertDetails.Person.PersonAlertDetails,
                Age: age,
              };
            }

            //automatic calculation of age when birthdate is there and age is missing from api
            const bDate =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.Birthdate
                : response?.Person?.PersonAlertDetails.Person?.Birthdate;
            const bAge =
              response?.Person?.PersonAlertDetails === null
                ? response?.Person?.PersonAlertDetails?.Age
                : response?.Person?.Age;
            if (
              (bDate !== '' || bDate !== null) &&
              (bAge === '' || bAge === null) &&
              bAge !== null &&
              bDate !== null
            ) {
              const age = calculateAgeAsString(bDate);
              state.alertDetails.Person.PersonAlertDetails.Age = age;
            }
          }),
        );
      }
      const storeValues = usePersonAlertStore.getState();
      const val = commonFormatterFunction(storeValues);
      set(
        produce(state => {
          state.initialAlert = val;
        }),
      );
    }
    if (
      get().userMode !== 'Edit' &&
      (useSaveChangesParamStore.getState().isDirty === false ||
        useSaveChangesParamStore.getState().isSettingsPage === true)
    ) {
      const storeValues = usePersonAlertStore.getState();
      const val = commonFormatterFunction(storeValues);
      set(
        produce(state => {
          state.initialAlert = val;
        }),
      );
    }

    set(
      produce(state => {
        state.isEditApiLoading = false;
        state.personApiCalled = true;
      }),
    );
  },

  checkDataHasChanged: () => {
    const store = usePersonAlertStore.getState();

    set({
      currentData: commonFormatterFunction(store),
    });

    if (Object.keys(get().initialAlert)?.length === 0) return true;

    if (get().personApiCalled === false && get().userMode === 'Edit') {
      useSaveChangesParamStore.getState().setIsDirty(false);
      return false;
    }

    if (
      JSON.stringify(get().currentData) === JSON.stringify(get().initialAlert)
    ) {
      useSaveChangesParamStore.getState().setIsDirty(false);
      return true;
    } else if (Object.keys(get().initialAlert).length > 0) {
      useSaveChangesParamStore.getState().setIsDirty(true);
      return false;
    } else if (
      get().userMode !== 'Edit' &&
      Object.keys(get().initialAlert).length === 0
    ) {
      useSaveChangesParamStore.getState().setIsDirty(false);
      const store = usePersonAlertStore.getState();
      set({
        initialAlert: commonFormatterFunction(store),
      });
      return false;
    } else {
      return false;
    }
  },

  updateAccountInfo: (value: AccountType | undefined) => {
    set(
      produce(state => {
        state.accountsInfo = value;
      }),
    );
  },

  updateUserMode: (value: string) => {
    set(
      produce(state => {
        state.userMode = value;
      }),
    );
  },

  setNewPerson: (val: boolean) => {
    set(
      produce(state => {
        state.newPerson = val;
      }),
    );
  },

  setSaveClicked: (value: boolean) => {
    set(
      produce(state => {
        state.isSaveClicked = value;
      }),
    );
  },

  enableAlertTabs: (type: string): boolean => {
    switch (type) {
      case 'accounts-tab':
        return get().accountsInfo !== undefined;
      case 'personInformation-tab': {
        const alertDetails = get().alertDetails;
        const accountsInfo = get().accountsInfo;
        const optionsByAccount = get().optionsByAccount;

        // Check if at least one value is selected from the dropdown(person alert)
        let hasSelectedDropdown = false;
        if (accountsInfo && accountsInfo.Id && optionsByAccount) {
          const opts = optionsByAccount[accountsInfo.Id];
          hasSelectedDropdown = Array.isArray(opts) && opts.length > 0;
        }

        //show alert only if the account has limiting values and has no options selected
        // const shouldDisable =
        //   hasSelectedDropdown === false &&
        //   (accountsInfo?.AccountCategoryLimitingValues?.length !== 0 ||
        //     accountsInfo?.AccountCategoryLimitingValues !== null);

        // Check if limiting values are coming from API
        const limitingValues = accountsInfo?.AccountCategoryLimitingValues;
        const hasLimitingValues =
          Array.isArray(limitingValues) && limitingValues.length > 0;

        // Disable when account has limiting values but no selection is made
        const shouldDisable = hasLimitingValues && !hasSelectedDropdown;

        return (
          alertDetails?.alertDate !== undefined &&
          alertDetails?.alertDate !== '' &&
          alertDetails?.Person?.FirstName !== undefined &&
          alertDetails?.Person?.FirstName !== '' &&
          alertDetails?.Person?.LastName !== undefined &&
          alertDetails?.Person?.LastName !== '' &&
          !shouldDisable
        );
      }
      case 'alert-tab':
        return (
          get().alertDetails?.SituationDescription !== undefined &&
          get().alertDetails?.SituationDescription !== '' &&
          !isTinymceEditorContentEmpty(
            get().alertDetails?.SituationDescription,
          ) &&
          (get().hasCallLimit === false ||
            (get()?.hasCallLimit === true &&
              get().alertDetails?.Person?.PersonCallLimit?.MaxCallCount !==
                undefined &&
              get().alertDetails?.Person?.PersonCallLimit?.MaxCallCount !==
                '' &&
              get().alertDetails?.Person?.PersonCallLimit?.MaxCallCount > 0 &&
              get().alertDetails?.Person?.PersonCallLimit?.ResetInterval !==
                undefined &&
              get().alertDetails?.Person?.PersonCallLimit?.ResetInterval !==
                '' &&
              get().alertDetails?.Person?.PersonCallLimit?.TimeZoneId !== '' &&
              get().alertDetails?.Person?.PersonCallLimit?.TimeZoneId !==
                undefined &&
              get().alertDetails?.Person?.PersonCallLimit?.TimeZoneId !== '-1'))
        );
      default:
        return false;
    }
  },

  updateCallLimit: (key: string, value: any) => {
    set(
      produce(state => {
        if (state.alertDetails?.Person === undefined) {
          state.alertDetails.Person = {
            ...state?.alertDetails?.Person,
          };
          state.alertDetails.Person['PersonCallLimit'] = {
            [key]: value,
          };
        } else {
          state.alertDetails.Person.PersonCallLimit = {
            ...state?.alertDetails?.Person?.PersonCallLimit,
            [key]: value,
          };
        }
      }),
    );
  },

  updateHasCallLimit: (value: boolean) => {
    set(
      produce(state => {
        state.hasCallLimit = value;

        if (value === false) {
          if (state.alertDetails.Person !== undefined) {
            state.alertDetails.Person.PersonCallLimit = null;
          } else
            state.alertDetails.Person = {
              ...state?.alertDetails?.Person,
            };
        }
      }),
    );
  },

  //setting the state values in to empty
  deleteAlertDetails: () => {
    set((state: any) => {
      return {
        ...state,
        alertDetails: {
          Person: undefined,
          alertDate: '',
          StartDate: '',
          EndDate: '',
          Reasons: [],
        },
        hasCallLimit: false,
        isSaveClicked: false,
        accountsInfo: undefined,
        showPrimaryNumberError: false,
        showSecondaryNumberError: false,
        initialAlert: {},
        newPerson: false,
        personApiCalled: false,
        //to clear the selected limter options
        optionsByAccount: {},
      };
    });
  },

  deletePersonDetails: () => {
    set((state: any) => {
      return {
        ...state,
        alertDetails: {
          ...state.alertDetails,
          Person: {},
        },
      };
    });
  },
  //to clear the selcted options by account
  clearOptionsByAccount: () => {
    set(
      produce(state => {
        state.optionsByAccount = {};
      }),
    );
  },

  disableSave: (): boolean => {
    const alertDetails = get().alertDetails;
    if (
      alertDetails?.StartDate === undefined ||
      alertDetails?.EndDate === undefined ||
      alertDetails?.Person?.FirstName === '' ||
      alertDetails?.Person?.FirstName === undefined ||
      alertDetails?.Person?.LastName === undefined ||
      alertDetails?.Person?.LastName === '' ||
      alertDetails.SituationDescription == '' ||
      alertDetails.SituationDescription == undefined
    )
      return true;
    return false;
  },

  setSearchPerson: (val: string) => {
    set(
      produce(state => {
        state.searchPerson = val;
      }),
    );
  },

  setShowTable: (val: boolean) => {
    set(
      produce(state => {
        state.showTable = val;
      }),
    );
  },

  setCurrentSortItem(val) {
    set(
      produce(state => {
        state.currentSortItem = val;
      }),
    );
  },

  setSelectedRowId(val) {
    set(
      produce(state => {
        state.selectedRowId = val;
      }),
    );
  },

  setSelectedAccountId(val) {
    set(
      produce(state => {
        state.selectedAccountId = val;
      }),
    );
  },

  updatePhnNumberError: (key: string, value: boolean) => {
    set(() => ({
      [key]: value,
    }));
  },

  setPersonApiCalled: (val: boolean) => {
    set(
      produce(state => {
        state.personApiCalled = val;
      }),
    );
  },

  setErrorMsgFalse: () => {
    set(
      produce(state => {
        state.showPrimaryNumberError = false;
        state.showSecondaryNumberError = false;
      }),
    );
  },
}));

function setPhoneNumber(phnNumber: any, countryId: any) {
  if (countryId === null) return formattedPhoneNumber(phnNumber, 1);
  return phnNumber;
}
